/**
 * FlagshipPracticeV4 — Wave 3 Practice scenes.
 *   Lever, Density, MeterBridge, Beetroot, Germination.
 */
import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Panel, Feedback, NextBar, Shell } from "./PracticeScaffold";

// ============================================================================
// LEVER — metre rule + fulcrum + load + effort
// ============================================================================
export const LeverPractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [E, setE] = useState(40);        // effort arm (cm right of fulcrum)
  const [P, setP] = useState(1);         // effort force (N)
  const [status, setStatus] = useState({});
  const [fb, setFb] = useState(null);
  const [logs, setLogs] = useState([]);
  const step = steps[stepIdx];

  const W = 2, L = 20;                   // fixed load and load-arm (left side)
  const balanced = Math.abs(W * L - P * E) < 1;
  const tilt = Math.atan((P * E - W * L) / 800) * (180 / Math.PI);

  const check = () => {
    const id = step.id;
    const targets = { p1: { E: 40, P: 1 }, p2: { E: 20, P: 2 }, p3: { E: 10, P: 4 } };
    if (id in targets) {
      const t = targets[id];
      const armOk = Math.abs(E - t.E) <= 3;
      const forceOk = Math.abs(P - t.P) <= 0.2;
      if (armOk && forceOk && balanced) {
        setStatus((s) => ({ ...s, [id]: "ok" }));
        setLogs((l) => [...l, { E, P, moment: (P * E).toFixed(1) }]);
        setFb({ type: "ok", msg: `${step.correct_hint} P × E = ${(P * E).toFixed(1)} · W × L = 40.` });
      } else if (!armOk) {
        setFb({ type: "warn", msg: `Move the effort to ${t.E} cm from the fulcrum.` });
      } else if (!forceOk) {
        setFb({ type: "warn", msg: `At E = ${t.E} cm, the balancing effort should be about ${t.P} N.` });
      } else {
        setFb({ type: "warn", msg: "Beam still tilting — nudge the effort so moments match." });
      }
    } else if (id === "p4") {
      setStatus((s) => ({ ...s, p4: "ok" }));
      setFb({ type: "ok", msg: step.correct_hint });
    }
  };
  const reset = () => { setE(40); setP(1); setStatus({}); setLogs([]); setFb(null); setStepIdx(0); };
  const canNext = status[step?.id] === "ok";
  const doNext = () => (stepIdx < steps.length - 1 ? (setStepIdx(stepIdx + 1), setFb(null)) : onComplete?.());

  const beamCx = 300, beamCy = 260;
  const beamHalf = 260;
  // load position on the beam
  const loadX = beamCx - (L / 50) * beamHalf;
  const effortX = beamCx + (E / 50) * beamHalf;

  const scene = (
    <svg viewBox="0 0 600 500" className="w-full h-full" data-testid="practice-canvas">
      {/* Fulcrum */}
      <polygon points={`${beamCx - 25},${beamCy + 12} ${beamCx},${beamCy} ${beamCx + 25},${beamCy + 12}`} fill="#94a3b8" stroke="#475569" strokeWidth="2" />
      <rect x={beamCx - 40} y={beamCy + 12} width="80" height="8" fill="#475569" />
      {/* Beam (rotates around fulcrum) */}
      <g transform={`rotate(${tilt}, ${beamCx}, ${beamCy})`}>
        <rect x={beamCx - beamHalf} y={beamCy - 6} width={beamHalf * 2} height="12" fill="#eab308" stroke="#a16207" strokeWidth="2" rx="2" />
        {/* Ruler ticks */}
        {[-50, -40, -30, -20, -10, 10, 20, 30, 40, 50].map((cm) => (
          <g key={cm}>
            <line x1={beamCx + (cm / 50) * beamHalf} y1={beamCy - 6} x2={beamCx + (cm / 50) * beamHalf} y2={beamCy - 12} stroke="#78350f" strokeWidth="1.5" />
            <text x={beamCx + (cm / 50) * beamHalf} y={beamCy - 16} fill="#a16207" fontSize="9" textAnchor="middle" fontFamily="monospace">{Math.abs(cm)}</text>
          </g>
        ))}
        {/* Load weight (fixed) */}
        <g transform={`translate(${loadX}, ${beamCy + 6})`}>
          <line x1="0" y1="0" x2="0" y2="24" stroke="#334155" strokeWidth="1.5" />
          <rect x="-18" y="24" width="36" height="36" fill="#f97316" stroke="#c2410c" strokeWidth="2" rx="3" />
          <text x="0" y="46" fill="#0f172a" fontSize="12" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{`${W} N`}</text>
        </g>
        {/* Effort weight */}
        <g transform={`translate(${effortX}, ${beamCy + 6})`}>
          <line x1="0" y1="0" x2="0" y2="24" stroke="#334155" strokeWidth="1.5" />
          <rect x="-18" y="24" width="36" height={20 + P * 8} fill="#22c55e" stroke="#166534" strokeWidth="2" rx="3" />
          <text x="0" y="42" fill="#0f172a" fontSize="12" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{`${P} N`}</text>
        </g>
      </g>
      {/* Status + moments */}
      <g transform="translate(30, 40)">
        <rect x="0" y="0" width="240" height="60" fill="#0f172a" stroke={balanced ? "#22c55e" : "#f59e0b"} strokeWidth="2" rx="6" />
        <text x="120" y="20" fill="#22d3ee" fontSize="10" textAnchor="middle" fontFamily="monospace">Load moment · Effort moment</text>
        <text x="120" y="44" fill={balanced ? "#22c55e" : "#f59e0b"} fontSize="16" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{`${W * L} N·cm  ${balanced ? "=" : "≠"}  ${(P * E).toFixed(0)} N·cm`}</text>
      </g>
      {logs.map((lg, k) => (
        <text key={k} x={30 + k * 130} y="470" fill="#94a3b8" fontSize="11" fontFamily="monospace">{`${lg.P}N × ${lg.E}cm = ${lg.moment}`}</text>
      ))}
    </svg>
  );
  const panel = (
    <>
      <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />
      <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4 space-y-4">
        <div>
          <div className="text-xs text-slate-500 mb-2 font-mono">Effort-arm distance E</div>
          <Slider min={5} max={50} step={1} value={[E]} onValueChange={(v) => setE(v[0])} data-testid="lever-arm" />
          <div className="mt-1 flex justify-between text-xs text-slate-500 font-mono"><span>5 cm</span><span className="text-cyan-300">{`E = ${E} cm`}</span><span>50 cm</span></div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-2 font-mono">Effort P</div>
          <Slider min={0.5} max={6} step={0.25} value={[P]} onValueChange={(v) => setP(v[0])} data-testid="lever-effort" />
          <div className="mt-1 flex justify-between text-xs text-slate-500 font-mono"><span>0.5 N</span><span className="text-cyan-300">{`P = ${P} N`}</span><span>6 N</span></div>
        </div>
        <Button size="sm" className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={check} data-testid="lever-check">Record balance</Button>
      </div>
      <Feedback msg={fb} />
      <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
    </>
  );
  return <Shell scene={scene} panel={panel} />;
};

// ============================================================================
// DENSITY — three blocks + balance + ruler
// ============================================================================
const BLOCKS = {
  glass:  { name: "Glass",     colour: "#7dd3fc", mass: 200,  L: 8, B: 5, T: 2, expected: 2.5 },
  wood:   { name: "Wood",      colour: "#a16207", mass: 48,   L: 8, B: 5, T: 2, expected: 0.6 },
  metal:  { name: "Aluminium", colour: "#e2e8f0", mass: 72.9, L: 3, B: 3, T: 3, expected: 2.7 },
};
export const DensityPractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState({});
  const [fb, setFb] = useState(null);
  const [logs, setLogs] = useState({});
  const step = steps[stepIdx];

  const tap = (key) => {
    setSelected(key);
    const targets = { p1: "glass", p2: "wood", p3: "metal" };
    if (targets[step.id] === key) {
      const b = BLOCKS[key];
      const V = b.L * b.B * b.T;
      const rho = b.mass / V;
      setStatus((s) => ({ ...s, [step.id]: "ok" }));
      setLogs((l) => ({ ...l, [key]: rho }));
      setFb({ type: "ok", msg: `${step.correct_hint} Measured ρ = ${rho.toFixed(2)} g/cm³.` });
    } else if (targets[step.id]) {
      setFb({ type: "warn", msg: `Wrong block — tap the ${BLOCKS[targets[step.id]].name} block.` });
    }
  };
  const verify = () => { setStatus((s) => ({ ...s, p4: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); };
  const reset = () => { setSelected(null); setStatus({}); setLogs({}); setFb(null); setStepIdx(0); };
  const canNext = status[step?.id] === "ok";
  const doNext = () => (stepIdx < steps.length - 1 ? (setStepIdx(stepIdx + 1), setFb(null)) : onComplete?.());

  const sel = selected ? BLOCKS[selected] : null;
  const V = sel ? sel.L * sel.B * sel.T : null;

  const scene = (
    <svg viewBox="0 0 600 500" className="w-full h-full" data-testid="practice-canvas">
      {/* Bench */}
      <rect x="20" y="360" width="560" height="10" fill="#334155" />
      {/* Balance pan */}
      <g transform="translate(80, 200)">
        <line x1="60" y1="0" x2="60" y2="140" stroke="#94a3b8" strokeWidth="3" />
        <ellipse cx="60" cy="0" rx="60" ry="12" fill="#94a3b8" opacity="0.8" />
        <ellipse cx="60" cy="0" rx="60" ry="12" fill="none" stroke="#475569" strokeWidth="2" />
        <text x="60" y="-24" fill="#22d3ee" fontSize="10" textAnchor="middle" fontFamily="monospace">balance</text>
        {sel && (
          <>
            <rect x="35" y="-30" width="50" height="30" fill={sel.colour} stroke="#334155" strokeWidth="1.5" rx="2" />
          </>
        )}
        <g transform="translate(0, 145)">
          <rect x="10" y="0" width="100" height="30" fill="#0f172a" stroke="#22c55e" strokeWidth="2" rx="4" />
          <text x="60" y="20" fill="#fde047" fontSize="14" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{sel ? `${sel.mass} g` : "— g"}</text>
        </g>
      </g>
      {/* Block choices */}
      {Object.entries(BLOCKS).map(([k, b], idx) => (
        <g key={k} transform={`translate(${300 + idx * 90}, 300)`} onClick={() => tap(k)} style={{ cursor: "pointer" }} data-testid={`density-block-${k}`}>
          <rect x="0" y="0" width="60" height="60" fill={b.colour} stroke={selected === k ? "#22d3ee" : "#334155"} strokeWidth={selected === k ? 3 : 2} rx="3" />
          <text x="30" y="80" fill="#94a3b8" fontSize="11" textAnchor="middle" fontFamily="monospace">{b.name}</text>
          <text x="30" y="94" fill="#64748b" fontSize="9" textAnchor="middle" fontFamily="monospace">{`${b.L}×${b.B}×${b.T} cm`}</text>
        </g>
      ))}
      {/* Density reading */}
      {sel && (
        <g transform="translate(80, 40)">
          <rect x="0" y="0" width="440" height="60" fill="#0f172a" stroke="#22c55e" strokeWidth="2" rx="6" />
          <text x="220" y="22" fill="#22d3ee" fontSize="10" textAnchor="middle" fontFamily="monospace">{`${sel.name}: mass ${sel.mass} g · vol ${V} cm³`}</text>
          <text x="220" y="46" fill="#fde047" fontSize="18" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{`ρ = ${(sel.mass / V).toFixed(2)} g/cm³ (expected ${sel.expected})`}</text>
        </g>
      )}
      {/* Recorded densities */}
      {Object.entries(logs).map(([k, rho], i) => (
        <text key={k} x={30 + i * 180} y="470" fill="#94a3b8" fontSize="11" fontFamily="monospace">{`${BLOCKS[k].name}: ${rho.toFixed(2)} g/cm³`}</text>
      ))}
    </svg>
  );
  const panel = (
    <>
      <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />
      {step?.id === "p4" && (
        <Button className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={verify} data-testid="density-verify">Verify against known</Button>
      )}
      <Feedback msg={fb} />
      <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
    </>
  );
  return <Shell scene={scene} panel={panel} />;
};

// ============================================================================
// METERBRIDGE — jockey slider, galvanometer, unknown X = 5 Ω
// ============================================================================
export const MeterBridgePractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [R, setR] = useState(2);          // known resistance in Ω (from set)
  const [l, setL] = useState(50);         // jockey position 1..99
  const [status, setStatus] = useState({});
  const [fb, setFb] = useState(null);
  const [logs, setLogs] = useState([]);
  const step = steps[stepIdx];

  const X = 5;                            // unknown resistance
  const balanceL = useMemo(() => (100 * R) / (R + X), [R]);
  const galv = Math.max(-40, Math.min(40, (l - balanceL) * 2)); // ±40° deflection

  const check = () => {
    const targets = { p1: { R: 2, l: 28.6 }, p2: { R: 5, l: 50 }, p3: { R: 10, l: 66.7 } };
    if (step.id in targets) {
      const t = targets[step.id];
      const R_ok = Math.abs(R - t.R) <= 0.4;
      const balanced = Math.abs(galv) <= 3;
      if (R_ok && balanced) {
        setStatus((s) => ({ ...s, [step.id]: "ok" }));
        const xCalc = R * (100 - l) / l;
        setLogs((v) => [...v, { R, l, X: xCalc }]);
        setFb({ type: "ok", msg: `${step.correct_hint} X calculated = ${xCalc.toFixed(2)} Ω.` });
      } else if (!R_ok) {
        setFb({ type: "warn", msg: `Set the resistance box to R = ${t.R} Ω first.` });
      } else {
        setFb({ type: "warn", msg: step.mistakes?.[0]?.hint || "Galvanometer still deflecting — nudge the jockey." });
      }
    } else if (step.id === "p4") {
      setStatus((s) => ({ ...s, p4: "ok" }));
      setFb({ type: "ok", msg: step.correct_hint });
    }
  };
  const reset = () => { setR(2); setL(50); setStatus({}); setLogs([]); setFb(null); setStepIdx(0); };
  const canNext = status[step?.id] === "ok";
  const doNext = () => (stepIdx < steps.length - 1 ? (setStepIdx(stepIdx + 1), setFb(null)) : onComplete?.());

  const wireY = 300;
  const wireX0 = 80, wireX1 = 520;
  const jockeyX = wireX0 + (l / 100) * (wireX1 - wireX0);

  const scene = (
    <svg viewBox="0 0 600 500" className="w-full h-full" data-testid="practice-canvas">
      {/* Wire */}
      <line x1={wireX0} y1={wireY} x2={wireX1} y2={wireY} stroke="#94a3b8" strokeWidth="4" />
      {/* Scale */}
      {[...Array(11)].map((_, k) => (
        <g key={k}>
          <line x1={wireX0 + k * 44} y1={wireY + 4} x2={wireX0 + k * 44} y2={wireY + 14} stroke="#64748b" strokeWidth="1.5" />
          <text x={wireX0 + k * 44} y={wireY + 30} fill="#64748b" fontSize="10" textAnchor="middle" fontFamily="monospace">{k * 10}</text>
        </g>
      ))}
      {/* Known R box */}
      <g transform={`translate(${wireX0 - 40}, ${wireY - 100})`}>
        <rect x="0" y="0" width="80" height="60" fill="#334155" stroke="#64748b" strokeWidth="2" rx="4" />
        <text x="40" y="22" fill="#22d3ee" fontSize="10" textAnchor="middle" fontFamily="monospace">Known R</text>
        <text x="40" y="44" fill="#fde047" fontSize="16" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{`${R} Ω`}</text>
      </g>
      {/* Unknown X box */}
      <g transform={`translate(${wireX1 - 40}, ${wireY - 100})`}>
        <rect x="0" y="0" width="80" height="60" fill="#334155" stroke="#64748b" strokeWidth="2" rx="4" />
        <text x="40" y="22" fill="#22d3ee" fontSize="10" textAnchor="middle" fontFamily="monospace">Unknown X</text>
        <text x="40" y="44" fill="#a5f3fc" fontSize="16" textAnchor="middle" fontFamily="monospace" fontWeight="bold">?</text>
      </g>
      {/* Wires to R and X */}
      <line x1={wireX0} y1={wireY} x2={wireX0} y2={wireY - 40} stroke="#94a3b8" strokeWidth="2" />
      <line x1={wireX1} y1={wireY} x2={wireX1} y2={wireY - 40} stroke="#94a3b8" strokeWidth="2" />
      {/* Battery */}
      <g transform="translate(280, 400)">
        <rect x="0" y="0" width="60" height="30" fill="#0f172a" stroke="#f97316" strokeWidth="2" rx="4" />
        <text x="30" y="20" fill="#f97316" fontSize="12" textAnchor="middle" fontFamily="monospace" fontWeight="bold">6 V</text>
      </g>
      {/* Galvanometer */}
      <g transform={`translate(${jockeyX - 25}, 130)`}>
        <circle cx="25" cy="25" r="30" fill="#0f172a" stroke={Math.abs(galv) <= 3 ? "#22c55e" : "#f59e0b"} strokeWidth="2" />
        <line x1="25" y1="25" x2={25 + 25 * Math.sin(galv * Math.PI / 180)} y2={25 - 25 * Math.cos(galv * Math.PI / 180)} stroke="#fde047" strokeWidth="2" />
        <circle cx="25" cy="25" r="3" fill="#fde047" />
        <text x="25" y="72" fill={Math.abs(galv) <= 3 ? "#22c55e" : "#f59e0b"} fontSize="10" textAnchor="middle" fontFamily="monospace">{Math.abs(galv) <= 3 ? "NULL" : `${galv > 0 ? "+" : ""}${galv.toFixed(0)}°`}</text>
      </g>
      {/* Jockey */}
      <g transform={`translate(${jockeyX}, ${wireY})`}>
        <line x1="0" y1="-50" x2="0" y2="-4" stroke="#94a3b8" strokeWidth="2" />
        <polygon points="-8,-4 8,-4 0,4" fill="#f97316" stroke="#c2410c" strokeWidth="1.5" />
      </g>
      {/* Readouts */}
      <g transform="translate(30, 40)">
        <rect x="0" y="0" width="220" height="60" fill="#0f172a" stroke="#22c55e" strokeWidth="2" rx="6" />
        <text x="110" y="22" fill="#22d3ee" fontSize="10" textAnchor="middle" fontFamily="monospace">{`Jockey · Balancing length l`}</text>
        <text x="110" y="46" fill="#fde047" fontSize="18" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{`l = ${l.toFixed(1)} cm`}</text>
      </g>
      {logs.map((lg, k) => (
        <text key={k} x={30 + k * 170} y="470" fill="#94a3b8" fontSize="11" fontFamily="monospace">{`R=${lg.R}Ω,l=${lg.l.toFixed(1)}→X=${lg.X.toFixed(2)}Ω`}</text>
      ))}
    </svg>
  );
  const panel = (
    <>
      <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />
      <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4 space-y-4">
        <div>
          <div className="text-xs text-slate-500 mb-2 font-mono">Known resistance R</div>
          <div className="flex gap-2">
            {[1, 2, 5, 10, 20].map((r) => (
              <button
                key={r}
                onClick={() => setR(r)}
                className={`flex-1 py-1.5 rounded-full text-xs font-mono font-bold ${R === r ? "bg-cyan-500 text-slate-950" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}
                data-testid={`bridge-R-${r}`}
              >
                {`${r}Ω`}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-2 font-mono">Jockey position along the wire</div>
          <Slider min={5} max={95} step={0.5} value={[l]} onValueChange={(v) => setL(v[0])} data-testid="bridge-jockey" />
          <div className="mt-1 flex justify-between text-xs text-slate-500 font-mono"><span>5</span><span className="text-cyan-300">{`l = ${l.toFixed(1)} cm`}</span><span>95</span></div>
        </div>
        <Button size="sm" className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={check} data-testid="bridge-check">Record balance</Button>
      </div>
      <Feedback msg={fb} />
      <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
    </>
  );
  return <Shell scene={scene} panel={panel} />;
};

// ============================================================================
// BEETROOT — 5 tubes at different temperatures
// ============================================================================
const BEETROOT_TUBES = [
  { temp: 0,  leak: 0.15 },
  { temp: 25, leak: 0.1 },
  { temp: 50, leak: 0.4 },
  { temp: 70, leak: 0.75 },
  { temp: 90, leak: 1.0 },
];
export const BeetrootPractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [selected, setSelected] = useState({});
  const [status, setStatus] = useState({});
  const [fb, setFb] = useState(null);
  const step = steps[stepIdx];

  const tap = (idx) => {
    const tube = BEETROOT_TUBES[idx];
    setSelected((s) => ({ ...s, [tube.temp]: tube.leak }));
    const targets = { p1: 25, p2: 50, p3: 90 };
    if (targets[step.id] === tube.temp) {
      setStatus((s) => ({ ...s, [step.id]: "ok" }));
      setFb({ type: "ok", msg: step.correct_hint });
    } else if (targets[step.id]) {
      setFb({ type: "warn", msg: `Try the ${targets[step.id]} °C tube instead.` });
    }
  };
  const verify = () => { setStatus((s) => ({ ...s, p4: "ok" })); setFb({ type: "ok", msg: step.correct_hint }); };
  const reset = () => { setSelected({}); setStatus({}); setFb(null); setStepIdx(0); };
  const canNext = status[step?.id] === "ok";
  const doNext = () => (stepIdx < steps.length - 1 ? (setStepIdx(stepIdx + 1), setFb(null)) : onComplete?.());

  const redColor = (leak) => `rgba(220, 38, ${Math.round(90 - leak * 60)}, ${0.15 + leak * 0.75})`;

  const scene = (
    <svg viewBox="0 0 600 500" className="w-full h-full" data-testid="practice-canvas">
      <rect x="20" y="420" width="560" height="10" fill="#334155" />
      {BEETROOT_TUBES.map((t, k) => {
        const x = 60 + k * 108;
        const leak = selected[t.temp] || 0;
        return (
          <g key={k} onClick={() => tap(k)} style={{ cursor: "pointer" }} data-testid={`beetroot-tube-${t.temp}`}>
            {/* Test tube */}
            <path d={`M ${x} 200 L ${x} 400 Q ${x} 420 ${x + 20} 420 L ${x + 50} 420 Q ${x + 70} 420 ${x + 70} 400 L ${x + 70} 200 Z`} fill="rgba(15,23,42,0.9)" stroke="#94a3b8" strokeWidth="2" />
            {/* Liquid */}
            <rect x={x + 3} y="240" width="64" height="177" fill={leak > 0 ? redColor(leak) : "rgba(219,234,254,0.4)"} />
            {/* Beetroot cube */}
            <rect x={x + 20} y="360" width="30" height="30" fill="#7f1d1d" stroke="#450a0a" strokeWidth="1.5" opacity={leak > 0 ? 0.4 + (1 - leak) * 0.5 : 0.9} />
            {/* Label */}
            <text x={x + 35} y="190" fill="#22d3ee" fontSize="12" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{`${t.temp} °C`}</text>
            {leak > 0 && (
              <text x={x + 35} y="440" fill="#94a3b8" fontSize="10" textAnchor="middle" fontFamily="monospace">{`leak ${(leak * 10).toFixed(1)}/10`}</text>
            )}
            {leak === 0 && (
              <text x={x + 35} y="440" fill="#64748b" fontSize="9" textAnchor="middle">tap</text>
            )}
          </g>
        );
      })}
      <text x="300" y="50" fill="#22d3ee" fontSize="14" textAnchor="middle" fontFamily="monospace">Beetroot cubes soaked at different temperatures</text>
      <text x="300" y="70" fill="#64748b" fontSize="10" textAnchor="middle">More red = more betalain leaked = more membrane damage</text>
    </svg>
  );
  const panel = (
    <>
      <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />
      {step?.id === "p4" && (
        <Button className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={verify} data-testid="beetroot-verify">Verify temperature trend</Button>
      )}
      <Feedback msg={fb} />
      <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
    </>
  );
  return <Shell scene={scene} panel={panel} />;
};

// ============================================================================
// GERMINATION — 5-day timeline
// ============================================================================
export const GerminationPractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [day, setDay] = useState(0);
  const [status, setStatus] = useState({});
  const [fb, setFb] = useState(null);
  const step = steps[stepIdx];

  const check = () => {
    const targets = { p1: 1, p2: 3, p3: 5 };
    if (step.id in targets) {
      const t = targets[step.id];
      if (Math.abs(day - t) <= 0.5) {
        setStatus((s) => ({ ...s, [step.id]: "ok" }));
        setFb({ type: "ok", msg: step.correct_hint });
      } else {
        setFb({ type: "warn", msg: `Slide the timeline to Day ${t}.` });
      }
    } else if (step.id === "p4") {
      setStatus((s) => ({ ...s, p4: "ok" }));
      setFb({ type: "ok", msg: step.correct_hint });
    }
  };
  const reset = () => { setDay(0); setStatus({}); setFb(null); setStepIdx(0); };
  const canNext = status[step?.id] === "ok";
  const doNext = () => (stepIdx < steps.length - 1 ? (setStepIdx(stepIdx + 1), setFb(null)) : onComplete?.());

  const swell = Math.min(1, day / 1);         // 0..1 by day 1
  const radicle = Math.max(0, Math.min(30, (day - 1) * 15)); // px
  const plumule = Math.max(0, Math.min(60, (day - 3) * 30));  // px
  const stageName = day < 1 ? "Dormant seed" : day < 3 ? "Imbibition + radicle emergence" : day < 5 ? "Root growth" : "Plumule (shoot) emergence";

  const scene = (
    <svg viewBox="0 0 600 500" className="w-full h-full" data-testid="practice-canvas">
      {/* Petri dish */}
      <ellipse cx="300" cy="330" rx="220" ry="60" fill="#0f172a" stroke="#94a3b8" strokeWidth="2" />
      <ellipse cx="300" cy="320" rx="220" ry="18" fill="#1e293b" stroke="#475569" strokeWidth="1.5" />
      {/* Cotton wool */}
      <ellipse cx="300" cy="316" rx="212" ry="14" fill="#f8fafc" opacity="0.6" />
      {/* Seeds */}
      {[-120, -60, 0, 60, 120].map((dx, k) => (
        <g key={k} transform={`translate(${300 + dx}, 316)`}>
          {/* Underground: radicle */}
          <line x1="0" y1="6" x2={k % 2 ? -4 : 4} y2={6 + radicle} stroke="#78350f" strokeWidth="2.5" />
          {radicle > 8 && <line x1={k % 2 ? -4 : 4} y1={6 + radicle * 0.5} x2={k % 2 ? -10 : 10} y2={6 + radicle * 0.8} stroke="#78350f" strokeWidth="1.5" />}
          {/* Seed */}
          <ellipse cx="0" cy="0" rx={7 + swell * 4} ry={5 + swell * 3} fill={day > 0 ? "#a16207" : "#78350f"} stroke="#451a03" strokeWidth="1.5" />
          {/* Above: plumule */}
          {plumule > 0 && (
            <>
              <line x1="0" y1="-4" x2="0" y2={-4 - plumule} stroke="#16a34a" strokeWidth="2.5" />
              <ellipse cx={-6} cy={-4 - plumule + 4} rx="8" ry="4" fill="#22c55e" />
              <ellipse cx={6} cy={-4 - plumule + 8} rx="8" ry="4" fill="#22c55e" />
            </>
          )}
        </g>
      ))}
      {/* Stage banner */}
      <g transform="translate(30, 30)">
        <rect x="0" y="0" width="240" height="60" fill="#0f172a" stroke="#22c55e" strokeWidth="2" rx="6" />
        <text x="120" y="22" fill="#22d3ee" fontSize="10" textAnchor="middle" fontFamily="monospace">{`Day ${day.toFixed(1)}`}</text>
        <text x="120" y="46" fill="#fde047" fontSize="14" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{stageName}</text>
      </g>
      {/* Underground line */}
      <line x1="80" y1="316" x2="520" y2="316" stroke="#a16207" strokeWidth="1" strokeDasharray="4 3" opacity="0.6" />
      <text x="80" y="336" fill="#a16207" fontSize="9" fontFamily="monospace">soil line</text>
    </svg>
  );
  const panel = (
    <>
      <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />
      <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4">
        <div className="text-xs text-slate-500 mb-2 font-mono">Timeline (days after sowing)</div>
        <Slider min={0} max={5} step={0.5} value={[day]} onValueChange={(v) => setDay(v[0])} data-testid="germination-day" />
        <div className="mt-3 flex justify-between text-xs text-slate-500 font-mono"><span>0</span><span className="text-cyan-300">{`Day ${day.toFixed(1)}`}</span><span>5</span></div>
        <Button size="sm" className="mt-3 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={check} data-testid="germination-check">Record this stage</Button>
      </div>
      <Feedback msg={fb} />
      <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
    </>
  );
  return <Shell scene={scene} panel={panel} />;
};
